import type { ExtraBundle, Lang, LocaleBundle, LocalePack, SectionsBundle } from "./types";
import en from "./locales/en";
import sectionsEn from "./sections/en";
import extraEn from "./extra/en";

const supported: Lang[] = ["en", "es", "fr", "pt", "it", "de", "ar"];

export const supportedLangs = supported;
export const LANG_STORAGE_KEY = "freecrm-lang";

export function detectLang(): Lang {
  if (typeof window !== "undefined") {
    try {
      const saved = window.localStorage.getItem(LANG_STORAGE_KEY) as Lang | null;
      if (saved && supported.includes(saved)) return saved;
    } catch {
      /* ignore */
    }
  }
  if (typeof navigator === "undefined") return "en";
  const langs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]) || [];
  for (const l of langs) {
    const code = l.toLowerCase().split("-")[0] as Lang;
    if (supported.includes(code)) return code;
  }
  return "en";
}

export function setLang(lang: Lang) {
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
  window.location.reload();
}

// English ships in the main bundle (it is the default and the prerendered HTML
// is English). Every other language is a separate chunk that is only fetched
// for visitors who actually use it, so ~40% of the old bundle no longer sits on
// the critical path.
const enPack: LocalePack = { lang: "en", locale: en, sections: sectionsEn, extra: extraEn };

const packLoaders: Record<Exclude<Lang, "en">, () => Promise<{ default: LocalePack }>> = {
  es: () => import("./packs/es"),
  fr: () => import("./packs/fr"),
  pt: () => import("./packs/pt"),
  it: () => import("./packs/it"),
  de: () => import("./packs/de"),
  ar: () => import("./packs/ar"),
};

let current: LocalePack = enPack;

/**
 * Loads the pack for `lang` and makes it the current one. Must resolve before
 * the first render (see main.tsx). Falls back to English if the chunk cannot
 * be fetched so the page still renders.
 */
export async function loadLocale(lang: Lang): Promise<LocalePack> {
  if (lang === "en") {
    current = enPack;
    return current;
  }
  try {
    current = (await packLoaders[lang]()).default;
  } catch {
    current = enPack;
  }
  return current;
}

export function getPack(): LocalePack {
  return current;
}

export function useLocale(): LocaleBundle {
  return current.locale;
}

export function useSections(): SectionsBundle {
  return current.sections;
}

export function useExtra(): ExtraBundle {
  return current.extra;
}

/** Localizes labels like "17 sec". */
export function formatDuration(label: string, unit: string) {
  const n = label.match(/\d+/)?.[0];
  return n ? `${n} ${unit}` : label;
}
